import React, { useState, useRef, useCallback } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Link, Loader2, CheckCircle, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Progress } from '../../ui/progress';
import { lambdaService } from '../../../services/lambdaService';

export const UrlDownloadNode: React.FC<{ data: { label: string; color: string; isTemplate?: boolean } }> = ({ data }) => {
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<'idle' | 'downloading' | 'done' | 'error'>('idle');
  const [fileName, setFileName] = useState('');
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleDownload = useCallback(async () => {
    if (!url.trim()) return;
    setStatus('downloading');
    setProgress(0);
    setErrorMsg('');
    try {
      const res = await lambdaService.downloadFromUrl(url);
      if (res.success && res.download_id) {
        setFileName(res.filename || 'file');
        // Poll for progress
        pollRef.current = setInterval(async () => {
          try {
            const poll = await lambdaService.getDownloadProgress(res.download_id!);
            if (poll.status === 'completed') {
              setStatus('done');
              setProgress(100);
              if (pollRef.current) clearInterval(pollRef.current);
            } else if (poll.status === 'failed') {
              setStatus('error');
              setErrorMsg(poll.error || 'Download failed');
              if (pollRef.current) clearInterval(pollRef.current);
            } else {
              setProgress(poll.progress || 0);
            }
          } catch {
            // ignore polling errors
          }
        }, 2000);
      } else if (res.success && !res.download_id) {
        // Instant download (no polling needed)
        setStatus('done');
        setProgress(100);
        setFileName(res.filename || 'file');
      } else {
        throw new Error(res.error || 'Download failed');
      }
    } catch (e: any) {
      setStatus('error');
      setErrorMsg(e.message || 'Download failed');
    }
  }, [url]);

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setUrl('');
    setStatus('idle');
    setProgress(0);
    setErrorMsg('');
    setFileName('');
  };

  return (
    <div className="bg-card border rounded-xl shadow-md w-[300px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <Link className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        {status === 'idle' && (
          <>
            <div className="nodrag">
              <Input
                className="h-7 text-[11px]"
                placeholder="https://example.com/data.csv"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleDownload()}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="w-full text-xs h-7"
              onClick={handleDownload}
              disabled={!url.trim()}
            >
              Download
            </Button>
          </>
        )}

        {status === 'downloading' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
              <span className="text-xs truncate">Downloading {fileName}...</span>
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>
        )}

        {status === 'done' && (
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
            <span className="text-xs truncate flex-1">{fileName}</span>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={reset}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-1">
            <p className="text-xs text-destructive">{errorMsg}</p>
            <Button variant="outline" size="sm" className="w-full text-xs h-7" onClick={reset}>
              Try again
            </Button>
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
    </div>
  );
};
